import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { PersonalDetails } from "./tabs/PersonalDetails";
import { PaymentDetails } from "./tabs/PaymentDetails";

type SettingsTab = "personal" | "payment";

export function AgencySettings() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get tab from URL query parameter
  const getTabFromQuery = (): SettingsTab => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'personal' || tab === 'payment') {
      return tab;
    }
    return 'personal';
  };
  
  const [activeTab, setActiveTab] = useState<SettingsTab>(getTabFromQuery());

  const tabs = [
    { id: "personal", label: "Personal Details" },
    { id: "payment", label: "Payment Details" },
  ] as const;

  // Clear any existing toasts when switching tabs
  useEffect(() => {
    toast.dismiss();
  }, [activeTab]);
  
  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    navigate({ search: params.toString() }, { replace: true });
  }, [activeTab, navigate, location.search]);
  
  // Update active tab when URL changes
  useEffect(() => {
    const tabFromQuery = getTabFromQuery();
    if (tabFromQuery !== activeTab) {
      setActiveTab(tabFromQuery);
    }
  }, [location.search]);

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">
          Manage your agency profile and account preferences
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-card rounded-lg shadow">
        <div className="border-b border-border">
          <div className="flex gap-6 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 text-sm font-medium relative ${
                  activeTab === tab.id
                    ? "text-brand-600"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-600" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Tab Content */}
          {activeTab === "personal" && <PersonalDetails />}
          {activeTab === "payment" && <PaymentDetails />}
        </div>
      </div>
    </div>
  );
}